import * as Yup from 'yup';
import moment from 'moment';
import {
  RunningPattern,
  BudgetType,
  ActionType,
} from 'core/rtbCampaign/singlePage/common';

const maxDayRange = 92;

interface CommonParams {
  defaultForm: any;
  actionType: ActionType;
  order: any;
  bidRange: number[];
}

export const getBasicValidationSchema = ({
  defaultForm,
  actionType,
  order,
  bidRange,
}: CommonParams) => {
  const campaignBudgetMinimum = order.campaignConstraint.campaignBudgetMinimum;

  return Yup.object().shape({
    advertiserId: Yup.number().required('這裡還是空白的哦！'),
    orderId: Yup.number().required('這裡還是空白的哦！'),
    name: Yup.string().required('這裡還是空白的哦！'),
    startDate: Yup.date().test(
      'min-date',
      '開始時間不能早於當下時間',
      (startDate) => {
        const alreadyRunning =
          actionType === ActionType.EDIT &&
          new Date(defaultForm.basic.startDate) <= new Date();

        if (alreadyRunning) {
          return true;
        }
        return moment(startDate).isAfter(moment());
      },
    ),
    endDate: Yup.date()
      .test(
        'max-date',
        `請選擇不超出${maxDayRange}天的時間區間`,
        (endDate, { parent }) =>
          moment(endDate).diff(parent.startDate, 'seconds') <=
          maxDayRange * 24 * 60 * 60,
      )
      .test('max-date', '無法選擇過去時間為結束時間', (endDate) =>
        moment(endDate).isAfter(moment()),
      ),
    budgetType: Yup.string().required('這裡還是空白的哦！'),
    budget: Yup.number()
      .transform((value) => (Number.isNaN(value) ? null : value))
      .test('is-toFixed-2', `預算僅支援小數點後兩位`, (value) => {
        const regex = /^-?\d+(\.\d{0,2})?$/;
        if (value === null) {
          return true;
        } else {
          return value !== undefined && regex.test(value.toString());
        }
      })
      .when('budgetType', {
        is: (val: string) => val === BudgetType.TOTAL,
        then: (schema) =>
          schema
            .required('這裡還是空白的哦！')
            .test(
              'is-positive',
              `預算不可低於廣告活動預算最小值${campaignBudgetMinimum}`,
              (value) => value >= campaignBudgetMinimum,
            )
            .test(
              'is-positive',
              `已超過總共可支配預算：${order.currency} ${order.budgetBalance}`,
              (value) => {
                const totalBudget = order.budgetBalance;
                let remainBudget;
                switch (actionType) {
                  case ActionType.EDIT:
                    if (value === defaultForm.basic.budget) return true;
                    remainBudget =
                      totalBudget - value + defaultForm.basic.budget;
                    break;
                  case ActionType.CREATE:
                  case ActionType.COPY:
                    remainBudget = totalBudget - value;
                    break;
                  default:
                    return value <= totalBudget;
                }
                return remainBudget >= 0;
              },
            )
            .test(
              'meets-expected-spent',
              `不可低於預期花費：${order.currency} ${defaultForm.basic.expectedSpent}`,
              (value) => value >= defaultForm.basic.expectedSpent,
            ),
      })
      .when('budgetType', {
        is: (val: string) => val === BudgetType.DAILY,
        then: (schema) =>
          schema
            .required('這裡還是空白的哦！')
            .test(
              'is-positive',
              `預算不可低於廣告活動預算最小值${campaignBudgetMinimum}`,
              (value) => value >= campaignBudgetMinimum,
            )
            .test(
              'is-positive',
              `已超過總共可支配預算：${order.currency} ${order.budgetBalance}`,
              (value) => {
                const totalBudget = order.budgetBalance;
                let remainBudget;
                switch (actionType) {
                  case ActionType.EDIT:
                    if (value === defaultForm.basic.budget) return true;
                    remainBudget =
                      totalBudget - value + defaultForm.basic.budget;
                    break;
                  case ActionType.CREATE:
                  case ActionType.COPY:
                    remainBudget = totalBudget - value;
                    break;
                  default:
                    return value <= totalBudget;
                }
                return remainBudget >= 0;
              },
            )
            .test(
              'meets-expected-spent',
              `不可低於預期花費：${order.currency} ${defaultForm.basic.expectedSpent}`,
              (value) => value >= defaultForm.basic.expectedSpent,
            ),
      })
      .when(['runningPattern', 'budgetType'], {
        is: (runningPattern, budgetType) =>
          runningPattern === RunningPattern.LIMITED &&
          budgetType === BudgetType.DAILY,
        then: (schema) =>
          schema
            .required('這裡還是空白的哦！')
            .test(
              'is-positive',
              `預算不可低於廣告活動預算最小值${campaignBudgetMinimum}`,
              (value) => value >= campaignBudgetMinimum,
            )
            .test(
              'is-positive',
              `已超過總共可支配預算：${order.currency} ${order.budgetBalance}`,
              (value) => {
                const totalBudget = order.budgetBalance;
                let remainBudget;
                switch (actionType) {
                  case ActionType.EDIT:
                    if (value === defaultForm.basic.budget) return true;
                    remainBudget =
                      totalBudget - value + defaultForm.basic.budget;
                    break;
                  case ActionType.CREATE:
                  case ActionType.COPY:
                    remainBudget = totalBudget - value;
                    break;
                  default:
                    return value <= totalBudget;
                }
                return remainBudget >= 0;
              },
            )
            .test(
              'meets-expected-spent',
              `不可低於預期花費：${order.currency} ${defaultForm.basic.expectedSpent}`,
              (value) => value >= defaultForm.basic.expectedSpent,
            ),
      }),
    dailyTargetBudget: Yup.number()
      .transform((value) => (Number.isNaN(value) ? null : value))
      .test('is-toFixed-2', `預算僅支援小數點後兩位`, (value) => {
        const regex = /^-?\d+(\.\d{0,2})?$/;
        if (value === null) {
          return true;
        } else {
          return value !== undefined && regex.test(value.toString());
        }
      })
      .when('budgetType', {
        is: (val: string) => val === BudgetType.DAILY,
        then: (schema) =>
          schema
            .required('這裡還是空白的哦！')
            .test(
              'is-positive',
              `預算不可低於廣告活動預算最小值${campaignBudgetMinimum}`,
              (value) =>
                value !== undefined &&
                value !== null &&
                value >= campaignBudgetMinimum,
            ),
      }),
    bidPrice: Yup.number()
      .required('這裡還是空白的哦！')
      .nullable()
      .transform((value) => (Number.isNaN(value) ? null : value))
      .test(
        'is-positive',
        `金額請高於0元以上哦!`,
        (value) => value !== undefined && value !== null && value > 0,
      )
      .test(
        'is-positive',
        `金額請低於${bidRange?.[1] || 0}元以下哦!`,
        (value) =>
          value !== undefined && value !== null && value <= bidRange?.[1],
      ),
  });
};
